/**
 * Reglage för batterikonfiguration och styrstrategi.
 *
 * Varje reglage har hjälptext som förklarar vad parametern faktiskt betyder.
 * Strategier är markerade med rekommendationsstatus så användaren förstår
 * vilka som är pålitliga och vilka som är experimentella.
 */

import type { BatteryConfig, Strategy } from "../sim/types";

interface BatteryControlsProps {
  battery: BatteryConfig;
  strategy: Strategy;
  fuseSize?: number;
  onBatteryChange: (battery: BatteryConfig) => void;
  onStrategyChange: (strategy: Strategy) => void;
}

interface StrategyInfo {
  label: string;
  description: string;
  badge?: "recommended" | "experimental";
}

const STRATEGY_INFO: Record<Strategy, StrategyInfo> = {
  "solar-only": {
    label: "Bara sol",
    description:
      "Batteriet laddas bara från solöverskott och urladdar mot import. Ingen prisstyrning. Pålitlig sommarstrategi men gör ingenting under vintern eftersom det inte finns solöverskott att absorbera. Når typiskt 30-50% av Optimal.",
  },
  "solar-night-charge": {
    label: "Sol + nattladdning",
    description:
      "Som ovan plus laddning från nätet de billigaste timmarna när det är ekonomiskt motiverat. Marginellt bättre när spotspread är stor. Når typiskt 40-60% av Optimal.",
  },
  "predictive-24h": {
    label: "24h-prediktion",
    description:
      "Rullande 24-timmarsoptimering. Pedagogisk heuristik som visar vad enkel smart styrning kan ge. Når typiskt 50-70% av Optimal. Tappar mest på flerdygnsplanering och kontinuerliga prisbeslut.",
    badge: "recommended",
  },
  "optimal-foresight": {
    label: "Optimal",
    description:
      "Dynamisk programmering med perfekt framförhållning över hela året. Teoretisk maxgräns. Verkliga kommersiella system (CheckWatt, Bydee, Tibber Smart Charging) når typiskt 70-85% av detta. Använd som referens för vad bästa möjliga styrning skulle ge.",
  },
};

export default function BatteryControls({
  battery,
  strategy,
  fuseSize,
  onBatteryChange,
  onStrategyChange,
}: BatteryControlsProps) {
  // Säkringen begränsar total husbelastning. Vid trefas:
  // maxHouseDraw [kW] ≈ 3 * fuseAmpere * 230 V / 1000 = 0.69 * fuseAmpere
  // Vid laddning från nätet lägger batteriet PÅ baslasten, så praktisk
  // maxgräns för batteri-effekten är (maxHouseDraw - typisk baslast 2-3 kW).
  const fuseMaxKW = fuseSize ? Math.round(3 * fuseSize * 230 / 1000) : null;
  const practicalMaxKW = fuseMaxKW ? Math.max(0, fuseMaxKW - 3) : null;
  const exceedsFuse =
    practicalMaxKW !== null && battery.maxPowerKW > practicalMaxKW;

  return (
    <section className="battery-controls">
      <h3>Batterikonfiguration</h3>

      <div className="slider-group">
        <SliderField
          label="Kapacitet"
          value={`${battery.capacityKWh} kWh`}
          helpText="Påverkar mest spotpris-besparingen och hur mycket sol du själv kan använda. Marginalnyttan minskar kraftigt över 12-15 kWh för normalhushåll. Dubbelt så stort batteri ger sällan dubbelt så mycket besparing."
          min={3}
          max={40}
          step={1}
          numericValue={battery.capacityKWh}
          minLabel="3 kWh"
          maxLabel="40 kWh"
          onChange={(v) =>
            onBatteryChange({ ...battery, capacityKWh: Math.round(v) })
          }
        />

        <SliderField
          label="Maxeffekt"
          value={`${battery.maxPowerKW} kW`}
          helpText="Påverkar mest stödtjänster (intäkt per kW maxeffekt) och effekttariffens peak-shaving. Mindre effekt på spotpris-arbitrage. På Bara sol-strategin kan hög maxeffekt faktiskt MINSKA besparingen eftersom batteriet dumpas snabbt."
          min={2}
          max={15}
          step={0.5}
          numericValue={battery.maxPowerKW}
          minLabel="2 kW"
          maxLabel="15 kW"
          onChange={(v) =>
            onBatteryChange({ ...battery, maxPowerKW: v })
          }
        />
        {exceedsFuse && fuseSize && (
          <p className="fuse-warning">
            Din {fuseSize}A trefas-säkring klarar max ~{fuseMaxKW} kW total
            husbelastning. Med typisk baslast 2-3 kW är praktisk gräns för
            batteri-laddning från nätet ~{practicalMaxKW} kW. Vid högre värden
            kommer ditt batteri-EMS dynamiskt sänka laddhastigheten på
            vintertimmar med hög baslast. Den teoretiska maxeffekten kommer
            inte att utnyttjas fullt ut.
          </p>
        )}

        <SliderField
          label="Pris (kr/kWh installerat)"
          value={`${Math.round((battery.investmentSek ?? 0) / battery.capacityKWh)} kr`}
          helpText="Påverkar ENDAST återbetalningstiden, inte den årliga besparingen. Marknadspriser 2026: 5000-9000 kr/kWh nyckelfärdigt för LFP-batterier av medelmärken. Lägre nivåer (1500-3000 kr/kWh) ses på kampanjerbjudanden eller okända märken — väg garanti och support mot priset."
          min={1500}
          max={12000}
          step={500}
          numericValue={Math.round(
            (battery.investmentSek ?? 7000 * battery.capacityKWh) /
              battery.capacityKWh,
          )}
          minLabel="1 500 kr"
          maxLabel="12 000 kr"
          onChange={(v) =>
            onBatteryChange({
              ...battery,
              investmentSek: Math.round(v) * battery.capacityKWh,
            })
          }
        />

        <SliderField
          label="Totalt pris"
          value={`${new Intl.NumberFormat("sv-SE").format(
            Math.round(battery.investmentSek ?? 0),
          )} kr`}
          helpText="Samma värde som kr/kWh ovan, fast som totalbelopp. Ändrar du den ena uppdateras den andra. Ange ditt nettopris efter Grön Teknik-avdrag (50% av total kostnad för batteri-installation, max 50 000 kr per person)."
          min={battery.capacityKWh * 1500}
          max={battery.capacityKWh * 12000}
          step={1000}
          numericValue={Math.round(battery.investmentSek ?? 0)}
          minLabel={`${new Intl.NumberFormat("sv-SE").format(
            battery.capacityKWh * 1500,
          )} kr`}
          maxLabel={`${new Intl.NumberFormat("sv-SE").format(
            battery.capacityKWh * 12000,
          )} kr`}
          onChange={(v) =>
            onBatteryChange({
              ...battery,
              investmentSek: Math.round(v),
            })
          }
        />
      </div>

      <h3 className="strategy-heading">Strategi</h3>
      <p className="hint hint-compact">
        Hur batteriet bestämmer när det ska ladda och urladda. Börja med
        Bara sol för pålitliga siffror.
      </p>
      <div className="strategy-selector">
        {(Object.keys(STRATEGY_INFO) as Strategy[]).map((s) => {
          const info = STRATEGY_INFO[s];
          return (
            <label key={s} className={strategy === s ? "selected" : ""}>
              <input
                type="radio"
                name="strategy"
                value={s}
                checked={strategy === s}
                onChange={() => onStrategyChange(s)}
              />
              <div>
                <div className="strategy-label-row">
                  <strong>{info.label}</strong>
                  {info.badge === "recommended" && (
                    <span className="badge badge-recommended">
                      Rekommenderad
                    </span>
                  )}
                  {info.badge === "experimental" && (
                    <span className="badge badge-experimental">
                      Experimentell
                    </span>
                  )}
                </div>
                <small>{info.description}</small>
              </div>
            </label>
          );
        })}
      </div>
    </section>
  );
}

function SliderField({
  label,
  value,
  helpText,
  min,
  max,
  step,
  numericValue,
  minLabel,
  maxLabel,
  onChange,
}: {
  label: string;
  value: string;
  helpText: string;
  min: number;
  max: number;
  step: number;
  numericValue: number;
  minLabel: string;
  maxLabel: string;
  onChange: (v: number) => void;
}) {
  return (
    <div className="slider-field">
      <div className="slider-header">
        <span className="slider-label">{label}</span>
        <strong className="slider-value">{value}</strong>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={numericValue}
        onChange={(e) => onChange(parseFloat(e.target.value))}
      />
      <div className="range-bounds">
        <small>{minLabel}</small>
        <small>{maxLabel}</small>
      </div>
      <p className="slider-help">{helpText}</p>
    </div>
  );
}
